/**
 * Кокпит — вечерний разбор на планшете.
 *
 * Телефонная раскладка на iPad оставляет две трети экрана пустыми и требует
 * ходить по вкладкам за тем, что могло бы лежать рядом. Здесь наоборот:
 * слева разделы, в середине работа, справа выезжает карточка камня — та же,
 * что на телефоне, со всеми её действиями. Меньше читать, больше кнопок:
 * любая строка и любая плитка нажимается.
 *
 * Кокпит ничего не снимает — он разбирает снятое: заявки покупателей,
 * витрина, дырки в данных за день, итог дня.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { db, enqueue, type LocalStone, type LocalSupplier, type LocalExhibition } from '../db';
import { apiFetch, syncNow, onSync } from '../sync';
import { loadRows, loadThumbs } from '../lib/catalog';
import { whereIs } from '../lib/where';
import StoneView from './StoneView';
import CertScan from './CertScan';
import Interpret from './Interpret';
import Tasks from './Tasks';
import Assistant from './Assistant';
import { TalkView } from './Talks';
import { Browse, Compare, shortNo } from './Browse';
import {
  ulid, plural, parseMoney, formatMoney, rowPerCt, rowCurrency, placeTitle,
  type StoneRow, type Status,
} from '@core';

type Section = 'day' | 'inbox' | 'orders' | 'shop' | 'catalog' | 'tasks' | 'assistant';

interface Order {
  id: string; stone_id: string; public_no: string | null; local_no: string | null;
  kind: string; jewelry_type: string | null; metal: string | null;
  buyer_name: string | null; buyer_contact: string; note: string | null;
  status: string; created_at: string;
}
interface Front {
  slug: string; enabled: boolean; title: string | null; url: string;
  inCatalog: number; promo: number; newOrders: number;
}

const JT_RU: Record<string, string> = { ring: 'кольцо', pendant: 'кулон', earrings: 'серьги' };
const MT_RU: Record<string, string> = { yellow: 'жёлтое золото', white: 'белое золото', rose: 'розовое золото' };

const PICKED: Status[] = ['interesting', 'strong', 'priority'];

function money(n: number | null | undefined, currency = 'USD'): string {
  if (n == null) return '—';
  const m = parseMoney(Math.round(n), currency);
  return m ? formatMoney(m) : String(n);
}

/** Короткая подпись камня — то, по чему его узнают, не открывая. */
function caption(r: StoneRow | undefined): string {
  if (!r) return '';
  const w = r.values.get('weight_ct')?.num;
  return [
    r.values.get('stone_type')?.text ?? r.values.get('variety')?.text,
    w != null ? `${Number(Number(w).toFixed(2))} ct` : null,
    r.values.get('color')?.text,
  ].filter(Boolean).join(' · ');
}

export default function Desk({ me, exhibition, onPhoneView }: {
  me: { name?: string; workspace_name?: string } | null;
  exhibition: LocalExhibition | null;
  onPhoneView: () => void;
}) {
  const [section, setSection] = useState<Section>('day');
  // Беседа на айпаде — лучший случай: крупный экран лежит между двумя людьми.
  const [talk, setTalk] = useState(false);
  // Беседа, открытая из задачи, — справа, как карточка камня.
  const [talkView, setTalkView] = useState<string | null>(null);

  // Данные всех разделов читаются разом: локальная база быстрая, а разделы
  // ссылаются друг на друга (итог дня показывает заявки, разбор — витрину).
  const [rows, setRows] = useState<StoneRow[]>([]);
  const [stones, setStones] = useState<Map<string, LocalStone>>(new Map());
  const [thumbs, setThumbs] = useState<Record<string, string>>({});
  const [suppliers, setSuppliers] = useState<LocalSupplier[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [front, setFront] = useState<Front | null>(null);
  const [offline, setOffline] = useState(false);

  // Карточка справа — стек: из камня можно открыть соседний по дублю.
  const [pane, setPane] = useState<string[]>([]);
  const [certFor, setCertFor] = useState<LocalStone | null>(null);
  const [compareIds, setCompareIds] = useState<string[] | null>(null);

  const [tick, setTick] = useState(0);
  const refresh = () => setTick((t) => t + 1);

  useEffect(() => {
    void (async () => {
      const [r, st, sup] = await Promise.all([
        loadRows(), db.stones.toArray(), db.suppliers.toArray(),
      ]);
      setRows(r);
      setStones(new Map(st.map((s) => [s.id, s])));
      setSuppliers(sup);
      setThumbs(await loadThumbs(r.map((x) => x.id)));
      try {
        const [or, fr] = await Promise.all([
          apiFetch('/api/orders'), apiFetch('/api/storefront'),
        ]);
        if (or.ok) setOrders(((await or.json()) as { orders: Order[] }).orders);
        if (fr.ok) setFront((await fr.json()) as Front);
        setOffline(!or.ok && !fr.ok);
      } catch { setOffline(true); }
    })();
  }, [tick]);

  // Синхронизация докатилась — на экране должно быть то же, что на сервере.
  const lastOk = useRef<string | undefined>(undefined);
  useEffect(() => onSync((s) => {
    if (s.lastOk && s.lastOk !== lastOk.current) { lastOk.current = s.lastOk; refresh(); }
  }), []);

  const rowById = useMemo(() => new Map(rows.map((r) => [r.id, r])), [rows]);

  // ── общие действия ────────────────────────────────────────────────────────

  const openStone = (id: string) => setPane((p) => [...p, id]);

  /** Флаги витрины уезжают полным слепком камня: частичный upsert затёр бы статус. */
  async function setShop(id: string, patch: { inCatalog?: boolean; promo?: boolean }) {
    const stone = stones.get(id);
    if (!stone) return;
    const next = { ...stone, ...patch };
    if (patch.inCatalog === false) next.promo = false;
    if (patch.promo) next.inCatalog = true;
    await db.stones.update(id, { inCatalog: next.inCatalog, promo: next.promo });
    await enqueue('stone.upsert', { ...next }, ulid());
    setStones((m) => new Map(m).set(id, next));
    void syncNow();
  }

  async function orderDone(id: string) {
    await apiFetch(`/api/orders/${id}/done`, { method: 'POST' });
    refresh();
  }

  const newOrders = orders.filter((o) => o.status === 'new');

  // ── разделы ───────────────────────────────────────────────────────────────

  const nav: Array<{ id: Section; cap: string; ico: string; badge?: number }> = [
    { id: 'day', cap: 'День', ico: '◉' },
    { id: 'inbox', cap: 'Разбор', ico: '▣' },
    { id: 'orders', cap: 'Заявки', ico: '✉', badge: newOrders.length },
    { id: 'shop', cap: 'Витрина', ico: '◆' },
    { id: 'catalog', cap: 'Каталог', ico: '⌕' },
    { id: 'tasks', cap: 'Задачи', ico: '☐' },
    { id: 'assistant', cap: 'Ассистент', ico: '✦' },
  ];

  return (
    <div className="desk">
      {talk && <Interpret exhibition={exhibition} onBack={() => setTalk(false)} />}
      <aside className="desk-rail">
        <div className="desk-brand">
          <b>GemsNote</b>
          <span>{me?.workspace_name ?? 'каталог'}</span>
        </div>
        <nav className="desk-nav">
          {nav.map((n) => (
            <button key={n.id} aria-current={section === n.id ? 'page' : undefined}
                    onClick={() => { setSection(n.id); setCompareIds(null); }}>
              <span className="ico">{n.ico}</span>
              <span className="cap">{n.cap}</span>
              {Boolean(n.badge) && <span className="badge">{n.badge}</span>}
            </button>
          ))}
          <button onClick={() => setTalk(true)}>
            <span className="ico">⇄</span>
            <span className="cap">Беседа</span>
          </button>
        </nav>
        <div className="desk-railfoot">
          {me?.name && <span className="who">{me.name}</span>}
          <button className="btn-quiet" onClick={onPhoneView}>Как на телефоне</button>
        </div>
      </aside>

      <main className="desk-main">
        {offline && section !== 'catalog' && (
          <div className="memo warn" style={{ margin: '16px 24px 0' }}>
            Нет связи с сервером: заявки и витрина недоступны, каталог — на месте.
          </div>
        )}

        {section === 'day' && (
          <DayBoard rows={rows} thumbs={thumbs} suppliers={suppliers}
                    exhibition={exhibition} newOrders={newOrders} front={front}
                    onOpenStone={openStone}
                    onOrders={() => setSection('orders')}
                    onInbox={() => setSection('inbox')} />
        )}
        {section === 'inbox' && (
          <Inbox rows={rows} thumbs={thumbs} onOpenStone={openStone} />
        )}
        {section === 'orders' && (
          <OrderBoard orders={orders} rowById={rowById} thumbs={thumbs}
                      onOpenStone={openStone} onDone={orderDone} />
        )}
        {section === 'shop' && (
          <ShopBoard rows={rows} stones={stones} thumbs={thumbs} front={front}
                     onOpenStone={openStone} onSetShop={setShop}
                     onToggleFront={async () => {
                       if (!front) return;
                       await apiFetch('/api/storefront', {
                         method: 'POST',
                         headers: { 'Content-Type': 'application/json' },
                         body: JSON.stringify({ enabled: !front.enabled }),
                       });
                       refresh();
                     }} />
        )}
        {section === 'tasks' && <Tasks key={talkView ?? ''} embedded onOpenTalk={setTalkView} />}
        {section === 'assistant' && <Assistant embedded />}
        {section === 'catalog' && (
          compareIds
            ? <Compare ids={compareIds} onBack={() => setCompareIds(null)} onOpenStone={openStone} />
            : <Browse onOpenStone={openStone} onCompare={setCompareIds} />
        )}
      </main>

      {/* Карточка камня — та же, что на телефоне, со всеми действиями. */}
      {pane.length > 0 && (
        <>
          <div className="desk-shade" onClick={() => setPane([])} />
          <div className="desk-pane">
            <StoneView
              stoneId={pane[pane.length - 1]}
              onBack={() => { setPane((p) => p.slice(0, -1)); refresh(); }}
              onOpenStone={openStone}
              onScanCert={(st) => setCertFor(st)}
            />
          </div>
        </>
      )}

      {talkView && (
        <>
          <div className="desk-shade" onClick={() => setTalkView(null)} />
          <div className="desk-pane">
            <TalkView talkId={talkView} onBack={() => setTalkView(null)} />
          </div>
        </>
      )}

      {certFor && (
        <CertScan stone={certFor} onDone={() => { setCertFor(null); refresh(); }}
                  onCancel={() => setCertFor(null)} />
      )}
    </div>
  );
}

// ── день ────────────────────────────────────────────────────────────────────

function DayBoard({ rows, thumbs, suppliers, exhibition, newOrders, front, onOpenStone, onOrders, onInbox }: {
  rows: StoneRow[]; thumbs: Record<string, string>;
  suppliers: LocalSupplier[]; exhibition: LocalExhibition | null;
  newOrders: Order[]; front: Front | null;
  onOpenStone: (id: string) => void; onOrders: () => void; onInbox: () => void;
}) {
  const midnight = new Date(); midnight.setHours(0, 0, 0, 0);
  const today = rows.filter((r) => new Date(r.capturedAt) >= midnight);
  const shown = today.length ? today : rows.slice().sort(
    (a, b) => b.capturedAt.localeCompare(a.capturedAt)).slice(0, 18);
  const picked = today.filter((r) => PICKED.includes(r.status as Status));
  const gaps = rows.filter((r) => needsWork(r)).length;
  const revisit = suppliers.filter((s) => s.followUp);

  const date = new Date().toLocaleDateString('ru-RU', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <div className="desk-scroll">
      <header className="desk-head">
        <h1>{date[0].toUpperCase() + date.slice(1)}</h1>
        <p>{exhibition ? placeTitle(exhibition) || exhibition.code : 'место не выбрано'}</p>
      </header>

      <div className="desk-tally">
        <button className={today.length ? 'on' : ''} onClick={onInbox}>
          <b>{today.length}</b><span>снято сегодня</span>
        </button>
        <button className={picked.length ? 'on' : ''} onClick={onInbox}>
          <b>{picked.length}</b><span>отобрано сегодня</span>
        </button>
        <button className={gaps ? 'on warn' : ''} onClick={onInbox}>
          <b>{gaps}</b><span>ждут разбора</span>
        </button>
        <button className={newOrders.length ? 'on' : ''} onClick={onOrders}>
          <b>{newOrders.length}</b><span>{plural(newOrders.length, 'новая заявка', 'новые заявки', 'новых заявок')}</span>
        </button>
        {front && (
          <button className={front.inCatalog ? 'on' : ''}>
            <b>{front.inCatalog}</b><span>в витрине</span>
          </button>
        )}
      </div>

      {newOrders.length > 0 && (
        <>
          <div className="head"><h3>Покупатели ждут ответа</h3>
            <button className="linky" onClick={onOrders}>все заявки ›</button></div>
          <div className="list">
            {newOrders.slice(0, 4).map((o) => (
              <button key={o.id} className="row" onClick={() => onOpenStone(o.stone_id)}>
                <span className="grow">
                  <span className="nm">{o.buyer_name || 'Покупатель'} · {o.buyer_contact}</span>
                  <span className="sub">
                    {o.public_no ?? o.local_no} · {o.kind === 'jewelry'
                      ? `${JT_RU[o.jewelry_type ?? ''] ?? 'изделие'}, ${MT_RU[o.metal ?? ''] ?? ''}`
                      : 'только камень'}
                  </span>
                </span>
                <span className="chev">›</span>
              </button>
            ))}
          </div>
        </>
      )}

      {revisit.length > 0 && (
        <>
          <div className="head"><h3>Вернуться завтра</h3><span>{revisit.length}</span></div>
          <div className="list">
            {revisit.map((s) => (
              <div key={s.id} className="row">
                <span className="grow">
                  <span className="nm">{s.companyName}</span>
                  <span className="sub">{[whereIs(s), s.city].filter(Boolean).join(' · ')}</span>
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="head">
        <h3>{today.length ? 'Снято сегодня' : 'Последние снятые'}</h3>
        <span>{shown.length}</span>
      </div>
      <StoneGrid rows={shown} thumbs={thumbs} onOpen={onOpenStone} />
    </div>
  );
}

// ── разбор ──────────────────────────────────────────────────────────────────

/** Чем камень не готов: эти дырки и есть вечерняя работа. */
function needsWork(r: StoneRow): boolean {
  return workTags(r).length > 0;
}
function workTags(r: StoneRow): string[] {
  const out: string[] = [];
  if (!r.values.get('asking_price') && !r.values.get('asking_price_per_ct')
      && !r.values.get('asking_price_per_ct_lot')) out.push('без цены');
  if (!r.values.get('stone_type')?.text && !r.values.get('variety')?.text) out.push('без вида');
  if (!r.values.get('weight_ct') && !r.values.get('weight_min_ct')) out.push('без веса');
  if (r.status === 'viewed') out.push('без решения');
  return out;
}

function Inbox({ rows, thumbs, onOpenStone }: {
  rows: StoneRow[]; thumbs: Record<string, string>; onOpenStone: (id: string) => void;
}) {
  // По умолчанию — сегодняшнее; но если сегодня разбирать нечего, а хвосты
  // есть, экран открывается сразу на них: пустой список с цифрой на «Дне»
  // выглядел бы враньём.
  const midnight = new Date(); midnight.setHours(0, 0, 0, 0);
  const todayHasWork = rows.some((r) => new Date(r.capturedAt) >= midnight && needsWork(r));
  const [scopeChoice, setScope] = useState<'today' | 'all' | null>(null);
  const scope = scopeChoice ?? (todayHasWork ? 'today' : 'all');

  const pool = scope === 'today'
    ? rows.filter((r) => new Date(r.capturedAt) >= midnight)
    : rows;
  const work = pool.filter(needsWork)
    .sort((a, b) => b.capturedAt.localeCompare(a.capturedAt));

  return (
    <div className="desk-scroll">
      <header className="desk-head">
        <h1>Разбор</h1>
        <p>Снятое, в котором чего-то не хватает. Нажмите камень — карточка откроется рядом.</p>
      </header>

      <div className="states" style={{ marginTop: 0 }}>
        <button className="state-btn" aria-pressed={scope === 'today'}
                onClick={() => setScope('today')}>Сегодня</button>
        <button className="state-btn" aria-pressed={scope === 'all'}
                onClick={() => setScope('all')}>За всё время</button>
      </div>

      {work.length === 0 ? (
        <div className="blankslate">
          <b>Всё разобрано</b>
          {scope === 'today'
            ? 'У снятого сегодня есть вид, вес, цена и решение.'
            : 'В каталоге нет камней с дырками в данных.'}
        </div>
      ) : (
        <div className="desk-worklist">
          {work.map((r) => (
            <button key={r.id} className="workrow" onClick={() => onOpenStone(r.id)}>
              {thumbs[r.id]
                ? <img src={thumbs[r.id]} alt="" />
                : <span className="ph">{r.kind === 'parcel' ? '⁘' : '◆'}</span>}
              <span className="grow">
                <span className="nm">{shortNo(r.no)}{caption(r) ? ` · ${caption(r)}` : ''}</span>
                <span className="sub">{[r.supplierName, r.where].filter(Boolean).join(' · ')}</span>
              </span>
              <span className="lack">
                {workTags(r).map((t) => <i key={t}>{t}</i>)}
              </span>
              <span className="chev">›</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── заявки ──────────────────────────────────────────────────────────────────

function OrderBoard({ orders, rowById, thumbs, onOpenStone, onDone }: {
  orders: Order[]; rowById: Map<string, StoneRow>; thumbs: Record<string, string>;
  onOpenStone: (id: string) => void; onDone: (id: string) => void;
}) {
  const fresh = orders.filter((o) => o.status === 'new');
  const done = orders.filter((o) => o.status !== 'new');
  const fmt = (d: string) => new Date(d).toLocaleString('ru-RU',
    { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

  const card = (o: Order, isNew: boolean) => (
    <div key={o.id} className={`ordercard${isNew ? '' : ' dim'}`}>
      <button className="stonepart" onClick={() => onOpenStone(o.stone_id)}>
        {thumbs[o.stone_id]
          ? <img src={thumbs[o.stone_id]} alt="" />
          : <span className="ph">◆</span>}
        <span>
          <b>{o.public_no ?? o.local_no}</b>
          <i>{caption(rowById.get(o.stone_id)) || 'открыть камень ›'}</i>
        </span>
      </button>
      <div className="buyerpart">
        <b>{o.buyer_name || 'Покупатель'}</b>
        <span className="contact">{o.buyer_contact}</span>
        <span className="what">
          {o.kind === 'jewelry'
            ? `${JT_RU[o.jewelry_type ?? ''] ?? 'изделие'} · ${MT_RU[o.metal ?? ''] ?? ''}`
            : 'только камень'}
        </span>
        {o.note && <q>{o.note}</q>}
        <span className="when">{fmt(o.created_at)}</span>
      </div>
      {isNew && (
        <button className="btn btn-primary donebtn" onClick={() => onDone(o.id)}>
          Обработана ✓
        </button>
      )}
    </div>
  );

  return (
    <div className="desk-scroll">
      <header className="desk-head">
        <h1>Заявки</h1>
        <p>{fresh.length
          ? `${fresh.length} ${plural(fresh.length, 'новая ждёт', 'новые ждут', 'новых ждут')} ответа`
          : 'Новых нет — все отвечены.'}</p>
      </header>

      {orders.length === 0 && (
        <div className="blankslate">
          <b>Заявок пока нет</b>
          Они появятся, когда покупатель выберет камень на витрине и оставит контакт.
        </div>
      )}

      {fresh.map((o) => card(o, true))}
      {done.length > 0 && (
        <>
          <div className="head"><h3>Обработанные</h3><span>{done.length}</span></div>
          {done.map((o) => card(o, false))}
        </>
      )}
    </div>
  );
}

// ── витрина ─────────────────────────────────────────────────────────────────

function ShopBoard({ rows, stones, thumbs, front, onOpenStone, onSetShop, onToggleFront }: {
  rows: StoneRow[]; stones: Map<string, LocalStone>; thumbs: Record<string, string>;
  front: Front | null;
  onOpenStone: (id: string) => void;
  onSetShop: (id: string, patch: { inCatalog?: boolean; promo?: boolean }) => void;
  onToggleFront: () => void;
}) {
  const [q, setQ] = useState('');
  const [qr, setQr] = useState('');

  useEffect(() => {
    if (!front?.enabled) { setQr(''); return; }
    void QRCode.toDataURL(front.url, { width: 320, margin: 1 }).then(setQr).catch(() => setQr(''));
  }, [front?.enabled, front?.url]);

  const needle = q.trim().toLowerCase();
  const match = (r: StoneRow) => !needle
    || r.no.toLowerCase().includes(needle)
    || caption(r).toLowerCase().includes(needle)
    || (r.supplierName ?? '').toLowerCase().includes(needle);

  const inShop = rows.filter((r) => stones.get(r.id)?.inCatalog && match(r));
  const rest = rows.filter((r) => !stones.get(r.id)?.inCatalog && match(r))
    .sort((a, b) => b.capturedAt.localeCompare(a.capturedAt));

  const tile = (r: StoneRow, shown: boolean) => {
    const st = stones.get(r.id);
    const perCt = rowPerCt(r);
    return (
      <div key={r.id} className="shoptile">
        <button className="pic" onClick={() => onOpenStone(r.id)}>
          {thumbs[r.id] ? <img src={thumbs[r.id]} alt="" /> : <span className="ph">◆</span>}
          {st?.promo && <span className="promotag">промо</span>}
        </button>
        <div className="cap">
          <b>{shortNo(r.no)}</b>
          <span>{caption(r) || 'без описания'}</span>
          {perCt != null && <i>{money(perCt, rowCurrency(r))}/ct</i>}
        </div>
        <div className="acts">
          {shown ? (
            <>
              <button className="state-btn" aria-pressed={Boolean(st?.promo)}
                      onClick={() => onSetShop(r.id, { promo: !st?.promo })}>
                {st?.promo ? 'В промо ✓' : 'В промо'}
              </button>
              <button className="state-btn" onClick={() => onSetShop(r.id, { inCatalog: false })}>
                Убрать
              </button>
            </>
          ) : (
            <button className="state-btn" onClick={() => onSetShop(r.id, { inCatalog: true })}>
              ＋ В каталог
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="desk-scroll">
      <header className="desk-head">
        <h1>Витрина</h1>
        <p>Что видят покупатели на {front ? front.url.replace('https://', '') : 'витрине'} —
          без цен, статусов и заметок.</p>
      </header>

      {front && (
        <div className="shopbar">
          <button className="state-btn" aria-pressed={front.enabled} onClick={onToggleFront}>
            {front.enabled ? 'Витрина открыта ✓' : 'Открыть витрину'}
          </button>
          {front.enabled && (
            <button className="state-btn" onClick={() => {
              void navigator.clipboard.writeText(front.url).catch(() => {});
            }}>Скопировать адрес</button>
          )}
          {qr && <img className="minicode" src={qr} alt="QR витрины" />}
        </div>
      )}

      <label className="field" style={{ maxWidth: 420 }}>
        <input value={q} onChange={(e) => setQ(e.target.value)}
               placeholder="Номер, вид или поставщик" />
      </label>

      <div className="head">
        <h3>В витрине</h3>
        <span>{inShop.length} · промо {inShop.filter((r) => stones.get(r.id)?.promo).length}</span>
      </div>
      {inShop.length === 0 ? (
        <div className="blankslate" style={{ padding: '20px' }}>
          <b>Витрина пуста</b>
          Добавьте камни из списка ниже — кнопка «В каталог» на каждой плитке.
        </div>
      ) : (
        <div className="shopgrid">{inShop.map((r) => tile(r, true))}</div>
      )}

      <div className="head"><h3>Остальные камни</h3><span>{rest.length}</span></div>
      <div className="shopgrid">{rest.map((r) => tile(r, false))}</div>
    </div>
  );
}

// ── плитки камней ───────────────────────────────────────────────────────────

function StoneGrid({ rows, thumbs, onOpen }: {
  rows: StoneRow[]; thumbs: Record<string, string>; onOpen: (id: string) => void;
}) {
  if (!rows.length) {
    return (
      <div className="blankslate">
        <b>Пока пусто</b>
        Снятое на телефоне появится здесь после синхронизации.
      </div>
    );
  }
  return (
    <div className="desk-grid">
      {rows.map((r) => (
        <button key={r.id} className="stone" onClick={() => onOpen(r.id)}
                aria-label={r.no}>
          {thumbs[r.id]
            ? <img src={thumbs[r.id]} alt="" />
            : <span className="blank">{r.kind === 'parcel' ? '⁘' : '◆'}</span>}
          {r.status !== 'viewed' && <span className={`state st-${r.status}`} />}
          <span className="tag">{shortNo(r.no)}</span>
        </button>
      ))}
    </div>
  );
}
